import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createUser,
  emailCredentials,
  listUsers,
  mailStatus,
  type AdminUserCreate,
} from "@/api/admin-api";
import type { Role } from "@/store/auth-store";
import { useToast } from "@/hooks/use-toast";
import { isValidEmail } from "@/lib/email";

import { MAIL_STATUS_KEY } from "./MailPanel";
import { USERS_KEY } from "./UserTable";
import {
  CREATABLE_ROLES,
  ROLE_META,
  passwordStrength,
} from "./lookups";

const USERNAME_PATTERN = /^[a-zA-Z0-9._-]+$/;
const USERNAME_MIN = 3;
const USERNAME_MAX = 64;

type AvailabilityState =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "available" }
  | { kind: "taken" }
  | { kind: "invalid"; reason: string };

export function CreateUserForm() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  // Opt IN, never a default. Mail leaves the building; sending one is
  // an act the admin should have chosen, not one they failed to
  // prevent. Ticking it is also refused outright when mail is not
  // configured, so the promise the tick makes can always be kept.
  const [emailLogin, setEmailLogin] = useState(false);
  const [location, setLocation] = useState("");
  // Account expiry. Only offered for a test candidate: clinical staff
  // accounts do not expire, and a date box on every user would invite
  // someone to put one there.
  const [expiresOn, setExpiresOn] = useState("");
  const [role, setRole] = useState<Role | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  // The availability check piggybacks on the cached user list. No
  // dedicated availability endpoint exists today, and adding one for
  // five admin users is overkill. Debounce purely so the comparison
  // doesn't churn the UI on every keystroke.
  const usersQuery = useQuery({
    queryKey: USERS_KEY,
    queryFn: () => listUsers(true),
  });

  const [debouncedUsername, setDebouncedUsername] = useState(username);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedUsername(username), 250);
    return () => clearTimeout(t);
  }, [username]);

  const availability: AvailabilityState = useMemo(() => {
    const u = debouncedUsername.trim();
    if (u.length === 0) return { kind: "idle" };
    if (u.length < USERNAME_MIN) {
      return { kind: "invalid", reason: `Min ${USERNAME_MIN} characters` };
    }
    if (u.length > USERNAME_MAX) {
      return { kind: "invalid", reason: `Max ${USERNAME_MAX} characters` };
    }
    if (!USERNAME_PATTERN.test(u)) {
      return {
        kind: "invalid",
        reason: "Letters, digits, dot, dash, underscore only",
      };
    }
    if (usersQuery.isLoading) return { kind: "checking" };
    const taken = (usersQuery.data ?? []).some(
      (other) => other.username.toLowerCase() === u.toLowerCase(),
    );
    return taken ? { kind: "taken" } : { kind: "available" };
  }, [debouncedUsername, usersQuery.data, usersQuery.isLoading]);

  const mail = useQuery({ queryKey: MAIL_STATUS_KEY, queryFn: mailStatus });
  const mailReady = mail.data?.available ?? false;
  // Was `includes("@")`, which unlocked the send on a single keystroke:
  // "@" and "a@" both passed. One shared validator now, so this screen
  // and the reset dialog cannot disagree about what is sendable.
  const emailLooksReal = isValidEmail(email);

  const strength = useMemo(() => passwordStrength(password), [password]);
  const passwordsMatch = password.length > 0 && password === confirm;

  const create = useMutation({
    mutationFn: (body: AdminUserCreate) => createUser(body),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      toast({
        title: "User created",
        description: `${created.username} (${ROLE_META[created.role].label}) added.`,
        variant: "success",
      });
      // The account exists either way. Mail is attempted AFTER, and its
      // failure is reported as its own thing -- a send that does not
      // land must never read as a create that did not happen.
      if (emailLogin && created.email) {
        const sent = password;
        emailCredentials(created.id, { password: sent, email: created.email })
          .then((r) =>
            toast({
              title: r.sent
                ? `Login sent to ${r.to}`
                : "The account was created, but the email did not send",
              ...(r.sent || !r.detail ? {} : { description: r.detail }),
              variant: r.sent ? "success" : "error",
              duration: r.sent ? 5_000 : 10_000,
            }),
          )
          .catch(() =>
            toast({
              title: "The account was created, but the email did not send",
              variant: "error",
            }),
          );
      }
      // Reset the form so a second create starts clean.
      setFullName("");
      setUsername("");
      setEmail("");
      setEmailLogin(false);
      setLicenseNumber("");
      setRole(null);
      setPassword("");
      setConfirm("");
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Failed to create user";
      toast({ title: "Create failed", description: detail, variant: "error" });
    },
  });

  const canSubmit =
    fullName.trim().length > 0 &&
    availability.kind === "available" &&
    role !== null &&
    passwordsMatch &&
    strength.score >= 2 &&
    !create.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !role) return;
    const body: AdminUserCreate = {
      username: username.trim(),
      full_name: fullName.trim(),
      role,
      password,
      ...(email.trim().length > 0 ? { email: email.trim() } : {}),
      ...(licenseNumber.trim().length > 0
        ? { license_number: licenseNumber.trim() }
        : {}),
      // Omitted when blank -> null -> unscoped (sees the whole site).
      ...(location.trim().length > 0 ? { location: location.trim() } : {}),
      // End of the chosen day, local time: an expiry of "the 30th" should
      // let them work ON the 30th, not lock them out at midnight as it
      // begins.
      ...(role === "TESTRAD" && expiresOn
        ? { access_expires_at: new Date(`${expiresOn}T23:59:59`).toISOString() }
        : {}),
    };
    create.mutate(body);
  };

  return (
    <form
      onSubmit={onSubmit}
      data-cy="create-user-form"
      className="grid grid-cols-1 gap-6 md:grid-cols-2"
    >
      {/* Left column: identity fields */}
      <div className="space-y-4 rounded-xl border border-border bg-background-card p-5 shadow-theme-sm">
        <h3 className="text-sm font-semibold text-foreground-heading">Identity</h3>

        <label className="block text-xs">
          <span className="sk-label">Full name</span>
          <input
            type="text"
            data-cy="input-fullname"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="sk-input mt-1"
            autoComplete="off"
          />
        </label>

        <label className="block text-xs">
          <span className="flex items-center justify-between">
            <span className="sk-label">Username</span>
            <UsernameAvailability state={availability} />
          </span>
          <input
            type="text"
            data-cy="input-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="sk-input mt-1 font-mono"
            autoComplete="off"
            spellCheck={false}
          />
        </label>

        <label className="block text-xs">
          <span className="sk-label">Email address</span>
          <input
            type="email"
            data-cy="input-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Optional for staff; needed to email a candidate their login"
            className="sk-input mt-1"
            autoComplete="off"
            spellCheck={false}
          />
        </label>

        <label className="block text-xs">
          <span className="sk-label">Licence number</span>
          <input
            type="text"
            data-cy="input-license"
            value={licenseNumber}
            onChange={(e) => setLicenseNumber(e.target.value)}
            placeholder="Optional, e.g. medical-council registration"
            className="sk-input mt-1 font-mono"
            autoComplete="off"
          />
        </label>

        <label className="block text-xs">
          <span className="sk-label">Location</span>
          <input
            type="text"
            data-cy="input-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Optional — leave blank to see all studies"
            className="sk-input mt-1 font-mono"
            autoComplete="off"
          />
          <span className="mt-1 block text-[11px] text-foreground-muted">
            Sonographers and reception only see studies matching their
            location. Leave blank for a single-site clinic.
          </span>
        </label>

        {role === "TESTRAD" && (
          <label className="block text-xs" data-cy="field-access-expiry">
            <span className="sk-label">Access expires</span>
            <input
              type="date"
              data-cy="input-access-expiry"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              className="sk-input mt-1"
            />
            <span className="mt-1 block text-[11px] text-foreground-muted">
              After this date the login stops working. Their assessment,
              recordings and scores are kept for review. Leave blank for no
              expiry.
            </span>
          </label>
        )}
      </div>

      {/* Right column: role + password */}
      <div className="space-y-4 rounded-xl border border-border bg-background-card p-5 shadow-theme-sm">
        <div>
          <h3 className="text-sm font-semibold text-foreground-heading">Role</h3>
          <p className="text-xs text-foreground-muted">
            Determines which screens and actions the user can access.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {CREATABLE_ROLES.map((r) => {
              const meta = ROLE_META[r];
              const selected = role === r;
              return (
                <button
                  key={r}
                  type="button"
                  data-cy={`role-card-${r}`}
                  onClick={() => setRole(r)}
                  className={[
                    "rounded-lg border px-3 py-2 text-left text-xs transition-colors",
                    selected
                      ? "border-accent bg-accent-light text-foreground ring-1 ring-accent"
                      : "border-border bg-background-card text-foreground-muted hover:bg-table-row-hover",
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium ${meta.badge}`}
                    >
                      {meta.label}
                    </span>
                    {selected ? (
                      <span className="text-[10px] font-semibold text-accent">
                        Selected
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1.5 text-[11px] leading-snug text-foreground-muted">
                    {meta.description}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-foreground-heading">Password</h3>
          <p className="text-xs text-foreground-muted">
            Min 10 characters. Communicate it out-of-band — the user should
            change it on first login.
          </p>

          <label className="mt-3 block text-xs">
            <span className="sk-label">Password</span>
            <input
              type="password"
              data-cy="input-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="sk-input mt-1"
            />
          </label>

          <div className="mt-2" data-cy="strength-meter">
            <div className="flex items-center justify-between text-xs">
              <span className="sk-label">Strength</span>
              <span
                data-cy="strength-label"
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${strength.pillTone}`}
              >
                {strength.label}
              </span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-border">
              <div
                className={`h-full ${strength.color} transition-all`}
                style={{ width: `${(strength.score / 4) * 100}%` }}
              />
            </div>
          </div>

          <label className="mt-3 block text-xs">
            <span className="sk-label">Confirm password</span>
            <input
              type="password"
              data-cy="input-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="sk-input mt-1"
            />
            {confirm.length > 0 && !passwordsMatch ? (
              <span
                data-cy="confirm-error"
                className="mt-1 inline-block text-[11px] text-status-urgent"
              >
                Passwords do not match
              </span>
            ) : null}
          </label>
        </div>

        {/* Emailing the login is OPT IN, and the reason it cannot be
            ticked is always stated -- a checkbox that is simply
            disabled teaches nothing. */}
        <label
          data-cy="field-email-login"
          className={[
            "flex items-start gap-2 rounded-lg border border-border px-3 py-2 text-xs",
            mailReady && emailLooksReal ? "text-foreground" : "text-foreground-muted",
          ].join(" ")}
        >
          <input
            type="checkbox"
            data-cy="input-email-login"
            checked={emailLogin && mailReady && emailLooksReal}
            disabled={!mailReady || !emailLooksReal}
            onChange={(e) => setEmailLogin(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-accent disabled:cursor-not-allowed disabled:opacity-40"
          />
          <span>
            Email them the address, username and password
            <span className="mt-0.5 block text-[11px] text-foreground-muted">
              {!mailReady
                ? (mail.data?.reason ??
                  "Mail is not configured — set it up under Admin → Mail.")
                : email.trim().length === 0
                  ? "Add an email address above to enable this."
                  : !emailLooksReal
                    ? `“${email.trim()}” is not a complete email address, so this cannot be sent.`
                    : `Sent to ${email.trim()} once the account is created.`}
            </span>
          </span>
        </label>

        <div className="flex justify-end pt-1">
          <button
            type="submit"
            data-cy="btn-create-user"
            disabled={!canSubmit}
            className="sk-btn sk-btn-primary"
          >
            {create.isPending ? "Creating…" : "Create user"}
          </button>
        </div>
      </div>
    </form>
  );
}

function UsernameAvailability({ state }: { state: AvailabilityState }) {
  if (state.kind === "idle") return null;
  if (state.kind === "checking") {
    return (
      <span data-cy="username-status" className="text-[10px] text-foreground-muted">
        Checking…
      </span>
    );
  }
  if (state.kind === "invalid") {
    return (
      <span data-cy="username-status" className="text-[10px] text-status-urgent">
        {state.reason}
      </span>
    );
  }
  if (state.kind === "taken") {
    return (
      <span data-cy="username-status" className="text-[10px] text-status-urgent">
        Already taken
      </span>
    );
  }
  return (
    <span data-cy="username-status" className="text-[10px] text-status-done">
      Available
    </span>
  );
}
